"use client";

import { errorLoggingService } from "@/lib/error-logging-service";
import { db } from "@/lib/firebase-client";
import { doc, getDoc, onSnapshot, type Unsubscribe } from "firebase/firestore";

const APP_SETTINGS_COLLECTION = "appSettings";
const APP_SETTINGS_DOC_ID = "settings";

export interface AppSettings {
  supportEmail?: string;
  showBetaBadge?: boolean;
  updatedAt?: Date;
  updatedBy?: string;
}

const DEFAULT_SETTINGS: AppSettings = {};

/**
 * Get app settings from Firestore
 * @returns App settings or default settings if not found
 */
export async function getAppSettings(): Promise<AppSettings> {
  try {
    const settingsDoc = await getDoc(doc(db, APP_SETTINGS_COLLECTION, APP_SETTINGS_DOC_ID));
    
    if (!settingsDoc.exists()) {
      return DEFAULT_SETTINGS;
    }

    const data = settingsDoc.data();
    return {
      supportEmail: data.supportEmail,
      showBetaBadge: data.showBetaBadge,
      updatedAt: data.updatedAt?.toDate(),
      updatedBy: data.updatedBy,
    };
  } catch (error) {
    errorLoggingService.logError({
      context: "getAppSettings",
      error: error as Error,
    });
    return DEFAULT_SETTINGS;
  }
}

/**
 * Subscribe to app settings changes
 * @param callback - Callback function called when settings change
 * @returns Unsubscribe function
 */
export function subscribeToAppSettings(callback: (settings: AppSettings) => void): Unsubscribe {
  const settingsRef = doc(db, APP_SETTINGS_COLLECTION, APP_SETTINGS_DOC_ID);
  
  return onSnapshot(
    settingsRef,
    (snapshot) => {
      if (!snapshot.exists()) {
        callback(DEFAULT_SETTINGS);
        return;
      }

      const data = snapshot.data();
      callback({
        supportEmail: data.supportEmail,
        showBetaBadge: data.showBetaBadge,
        updatedAt: data.updatedAt?.toDate(),
        updatedBy: data.updatedBy,
      });
    },
    (error) => {
      errorLoggingService.logError({
        context: "subscribeToAppSettings",
        error: error as Error,
      });
      callback(DEFAULT_SETTINGS);
    }
  );
}

/**
 * Update app settings (admin only)
 * @param settings - Settings to update (partial)
 */
export async function updateAppSettings(settings: {
  supportEmail?: string;
  showBetaBadge?: boolean;
}): Promise<void> {
  const { getClientFunctions } = await import("@/lib/firebase-client");
  const functions = getClientFunctions();
  if (!functions) {
    throw new Error("Firebase Functions not available");
  }
  const { httpsCallable } = await import("firebase/functions");
  const updateAppSettingsFn = httpsCallable(functions, "updateAppSettings");
  
  await updateAppSettingsFn(settings);
}

